import { useState, useRef, useEffect } from "react";
import { useModalStore } from "../../../stores/modalStore.js";
import { useChannelStore } from "../../../stores/channelStore.js";
import { useServerStore } from "../../../stores/serverStore.js";

export function SearchModal() {
  const { isOpen, type, onClose } = useModalStore();
  const channels       = useChannelStore(s => s.channels);
  const currentServerId = useServerStore(s => s.currentServerId);

  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selChan, setSelChan] = useState('all');
  const [filter,  setFilter]  = useState('all'); // all | mentions | files
  const inputRef  = useRef(null);
  const debounce  = useRef(null);

  useEffect(() => { if (isOpen && type === 'search') { setQuery(''); setResults([]); setTimeout(() => inputRef.current?.focus(), 50); } }, [isOpen, type]);

  useEffect(() => {
    if (!query.trim() || query.length < 2) { setResults([]); return; }
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => doSearch(query), 300);
    return () => { if (debounce.current) clearTimeout(debounce.current); };
  }, [query, selChan]);

  if (!isOpen || type !== 'search') return null;

  const textChannels = channels.filter(c => c.type !== 'voice');

  const doSearch = async (q) => {
    setLoading(true);
    const hits = [];
    const targets = selChan === 'all' ? textChannels : textChannels.filter(c => c.id === selChan);
    const token = localStorage.getItem('auth_token');
    await Promise.all(
      targets.map(async (ch) => {
        try {
          const res = await fetch(`/api/servers/${currentServerId}/channels/${ch.id}/search?q=${encodeURIComponent(q)}&limit=10`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const d = await res.json();
          if (d.success) {
            hits.push(...(d.results || []).map(m => ({ ...m, channelName: ch.name, channelId: ch.id })));
          }
        } catch (_) {}
      })
    );
    // Sort by timestamp desc
    hits.sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
    setResults(hits.slice(0, 50));
    setLoading(false);
  };

  const highlight = (text, q) => {
    if (!q || !text) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text.slice(0, 120);
    const start = Math.max(0, idx - 30);
    const chunk = text.slice(start, start + 160);
    const parts = chunk.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((p, i) =>
      p.toLowerCase() === q.toLowerCase()
        ? <mark key={i} style={{ background: 'rgba(99,102,241,0.4)', color: '#fff', borderRadius: 2, padding: '0 1px' }}>{p}</mark>
        : p
    );
  };

  const jumpTo = (result) => {
    onClose();
    // Switch channel then scroll
    useChannelStore.getState().setCurrentChannel?.(result.channelId);
    setTimeout(() => {
      const el = document.getElementById(`msg-${result.id}`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('msg-highlight'); setTimeout(() => el.classList.remove('msg-highlight'), 2000); }
    }, 400);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, paddingTop: '8vh' }} onClick={onClose}>
      <div style={{ width: '100%', maxWidth: 640, maxHeight: '78vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary,#13141f)', borderRadius: 16, boxShadow: '0 32px 80px rgba(0,0,0,0.7)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}>

        {/* Search bar */}
        <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18, opacity: 0.4 }}>🔍</span>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search messages…"
            style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: '#fff', fontSize: 16, fontFamily: 'inherit' }}
          />
          {query && (
            <button onClick={() => { setQuery(''); setResults([]); }} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>✕</button>
          )}
          <kbd style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.4)' }}>Esc</kbd>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, padding: '8px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', overflowX: 'auto' }}>
          <select value={selChan} onChange={e => setSelChan(e.target.value)}
            style={{ padding: '4px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 12, outline: 'none', cursor: 'pointer', flexShrink: 0 }}>
            <option value="all">All channels</option>
            {textChannels.map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
          </select>
          {['all', 'mentions', 'files'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ padding: '4px 12px', borderRadius: 6, fontSize: 12, border: '1px solid', cursor: 'pointer', transition: 'all 0.15s', flexShrink: 0,
                background: filter === f ? 'rgba(99,102,241,0.25)' : 'rgba(255,255,255,0.05)',
                borderColor: filter === f ? 'rgba(99,102,241,0.5)' : 'rgba(255,255,255,0.1)',
                color: filter === f ? '#a5b4fc' : 'rgba(255,255,255,0.5)',
              }}>
              {f === 'all' ? '📝 Messages' : f === 'mentions' ? '@ Mentions' : '📎 Files'}
            </button>
          ))}
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>Searching…</div>
          ) : !query.trim() ? (
            <div style={{ padding: '40px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
              <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)' }}>Search for messages, files, or mentions</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.2)', marginTop: 6 }}>Type at least 2 characters</div>
            </div>
          ) : results.length === 0 ? (
            <div style={{ padding: '40px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>😶</div>
              <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)' }}>No results for "{query}"</div>
            </div>
          ) : (
            <>
              <div style={{ padding: '8px 16px', fontSize: 11, color: 'rgba(255,255,255,0.3)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                {results.length} result{results.length !== 1 ? 's' : ''}
              </div>
              {results.map((r, i) => (
                <div key={r.id || i} onClick={() => jumpTo(r)}
                  style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#06b6d4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                      {(r.author || r.senderName || '?')[0]?.toUpperCase()}
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{r.author || r.senderName}</span>
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
                      in #{r.channelName} · {r.timestamp ? new Date(r.timestamp).toLocaleDateString() : ''}
                    </span>
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(99,102,241,0.7)' }}>Jump →</span>
                  </div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', paddingLeft: 36, lineHeight: 1.5 }}>
                    {highlight(r.content || r.text || '', query)}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
